import { Link } from "react-router-dom";
import type { Article } from "@/types";
import { formatViews } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  variant?: "vertical" | "horizontal" | "compact";
  showExcerpt?: boolean;
}

export default function ArticleCard({
  article,
  variant = "vertical",
  showExcerpt = true,
}: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex cursor-pointer gap-3 border-b border-background-200/70 py-3.5 last:border-0"
      >
        <div className="h-[68px] w-[100px] shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-between py-0.5">
          <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="text-primary-600">{article.categoryName}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex cursor-pointer gap-4 rounded-lg border border-background-200/80 bg-background-50 p-3 transition-colors hover:border-primary-300"
      >
        <div className="h-[110px] w-[150px] shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <span className="mb-1 text-xs font-medium text-primary-600">{article.categoryName}</span>
          <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          {showExcerpt && (
            <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-foreground-500">
              {article.excerpt}
            </p>
          )}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
          {article.categoryName}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
        {showExcerpt && (
          <p className="mt-2 line-clamp-2 flex-1 text-sm leading-6 text-foreground-500">
            {article.excerpt}
          </p>
        )}
        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line" aria-hidden="true" />
            {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}